/**
 * mandate-registration-reply.ts - the insurer's answer to a registration (Stefan P5 V2.0 steps 25 and
 * 28, Kap. 7.12, 7.13, 7.15): the reply vocabulary, how a mail was matched, the policy's derived
 * registration status and its words, the eligibility date of an inherited policy, the plausibility
 * window of a Wirkungsdatum, and the wire DTOs of the decisions.
 *
 * Pure data and pure functions only, shared by api and web.
 */

import type { PolicyStatus } from './policy';
import type { MandateInsurerNotificationStatus } from './versicherer-benachrichtigung';

// ── the parsed reply ────────────────────────────────────────────────────────────────────────

/** What the insurer answered. */
export const REGISTRATION_REPLY_OUTCOME = {
  EINGETRAGEN: 'eingetragen',
  ABGELEHNT: 'abgelehnt',
  RUECKFRAGE: 'rueckfrage',
  UNKLAR: 'unklar',
  /** An answer to an Auflösungsmeldung: the insurer de-registered the broker. */
  AUSGETRAGEN: 'ausgetragen',
} as const;
export type RegistrationReplyOutcome = (typeof REGISTRATION_REPLY_OUTCOME)[keyof typeof REGISTRATION_REPLY_OUTCOME];
export const REGISTRATION_REPLY_OUTCOME_VALUES: readonly RegistrationReplyOutcome[] = ['eingetragen', 'abgelehnt', 'rueckfrage', 'unklar', 'ausgetragen'];
export const REGISTRATION_REPLY_OUTCOME_LABEL: Record<RegistrationReplyOutcome, string> = {
  eingetragen: 'Eingetragen',
  abgelehnt: 'Abgelehnt',
  rueckfrage: 'Rückfrage',
  unklar: 'Unklar',
  ausgetragen: 'Ausgetragen',
};

/** How an inbound mail was matched to its registration. */
export const REGISTRATION_REPLY_MATCH = {
  THREAD: 'thread',
  REFERENCE: 'reference',
  DOMAIN: 'domain',
  MANUAL: 'manual',
} as const;
export type RegistrationReplyMatch = (typeof REGISTRATION_REPLY_MATCH)[keyof typeof REGISTRATION_REPLY_MATCH];

/**
 * How sure each match is. The routed confidence is the lower of this and the parser's confidence, so
 * a domain-only match never clears the default auto threshold (0.900).
 */
export const REGISTRATION_MATCH_SCORE: Record<RegistrationReplyMatch, number> = {
  thread: 1,
  reference: 0.95,
  domain: 0.8,
  manual: 1,
};

/** A human's decision on a reviewed reply. */
export const REGISTRATION_REPLY_DECISION = {
  UEBERNOMMEN: 'uebernommen',
  KORRIGIERT: 'korrigiert',
  VERWORFEN: 'verworfen',
} as const;
export type RegistrationReplyDecision = (typeof REGISTRATION_REPLY_DECISION)[keyof typeof REGISTRATION_REPLY_DECISION];

/** Why an insurer refused the registration (Kap. 7.15). */
export const REGISTRATION_REJECTION_REASON_VALUES = [
  'exklusivagentur',
  'kunde_unbekannt',
  'police_unbekannt',
  'mandat_unvollstaendig',
  'sonstiges',
] as const;
export type RegistrationRejectionReason = (typeof REGISTRATION_REJECTION_REASON_VALUES)[number];
export const REGISTRATION_REJECTION_REASON_LABEL: Record<RegistrationRejectionReason, string> = {
  exklusivagentur: 'Exklusivagentur',
  kunde_unbekannt: 'Kunde unbekannt',
  police_unbekannt: 'Police unbekannt',
  mandat_unvollstaendig: 'Mandat unvollständig',
  sonstiges: 'Sonstiges',
};

/** Why a broker moves a confirmed registration back to Klärung (the Korrektur, Stefan #14). */
export const REGISTRATION_KORREKTUR_REASON_VALUES = ['falsches_wirkungsdatum', 'falsche_zuordnung', 'versicherer_widerruft', 'sonstiges'] as const;
export type RegistrationKorrekturReason = (typeof REGISTRATION_KORREKTUR_REASON_VALUES)[number];
export const REGISTRATION_KORREKTUR_REASON_LABEL: Record<RegistrationKorrekturReason, string> = {
  falsches_wirkungsdatum: 'Falsches Wirkungsdatum',
  falsche_zuordnung: 'Falsche Zuordnung',
  versicherer_widerruft: 'Versicherer widerruft',
  sonstiges: 'Sonstiges',
};

/** Why a broker lifts a policy's registration exception (required, audited). */
export const REGISTRATION_EXCEPTION_LIFT_REASON_VALUES = ['versicherer_korrigiert', 'irrtum', 'sonstiges'] as const;
export type RegistrationExceptionLiftReason = (typeof REGISTRATION_EXCEPTION_LIFT_REASON_VALUES)[number];
export const REGISTRATION_EXCEPTION_LIFT_REASON_LABEL: Record<RegistrationExceptionLiftReason, string> = {
  versicherer_korrigiert: 'Versicherer hat korrigiert',
  irrtum: 'Irrtümlich ausgenommen',
  sonstiges: 'Sonstiges',
};

/** How a match is named in the review. */
export const REGISTRATION_REPLY_MATCH_LABEL: Record<RegistrationReplyMatch, string> = {
  thread: 'Antwort im Mailverlauf',
  reference: 'Vorgangs-ID',
  domain: 'Absenderdomain',
  manual: 'Manuell zugeordnet',
};

/** A per-policy exception a broker sets (the insurer's own exclusion comes from its reply). */
export const POLICY_REGISTRATION_EXCEPTION_REASON_VALUES = ['versicherer_ausgenommen', 'broker', 'vn_abweichend'] as const;
export type PolicyRegistrationExceptionReason = (typeof POLICY_REGISTRATION_EXCEPTION_REASON_VALUES)[number];
export const BROKER_POLICY_EXCEPTION_REASON_VALUES = ['broker', 'vn_abweichend'] as const;
export type BrokerPolicyExceptionReason = (typeof BROKER_POLICY_EXCEPTION_REASON_VALUES)[number];

// ── the policy's derived registration status (Kap. 7.12, 7.14) ──────────────────────────────

export const POLICY_REGISTRATION_STATUS = {
  NICHT_REGISTRIERT: 'nicht_registriert',
  GEMELDET: 'gemeldet',
  ERINNERT: 'erinnert',
  KLAERUNG: 'klaerung',
  RUECKFRAGE: 'rueckfrage',
  EINGETRAGEN: 'eingetragen',
  EINGETRAGEN_VERERBT: 'eingetragen_vererbt',
  AUSGENOMMEN: 'ausgenommen',
  NICHT_COURTAGEBERECHTIGT: 'nicht_courtageberechtigt',
  GESCHLOSSEN: 'geschlossen',
  HISTORISCH: 'historisch',
  /** The registration was de-registered at the insurer (a mandate termination). */
  AUSGETRAGEN: 'ausgetragen',
} as const;
export type PolicyRegistrationStatus = (typeof POLICY_REGISTRATION_STATUS)[keyof typeof POLICY_REGISTRATION_STATUS];

/** Stefan's words for a policy's registration (the one place they live). */
export const POLICY_REGISTRATION_LABEL: Record<PolicyRegistrationStatus, string> = {
  nicht_registriert: 'Nicht registriert',
  gemeldet: 'Gemeldet',
  erinnert: 'Erinnert',
  klaerung: 'Klärung',
  rueckfrage: 'Rückfrage',
  eingetragen: 'Eingetragen',
  eingetragen_vererbt: 'Eingetragen (vererbt)',
  ausgenommen: 'Ausgenommen',
  nicht_courtageberechtigt: 'Nicht courtageberechtigt',
  geschlossen: 'Geschlossen',
  historisch: 'Historisch',
  ausgetragen: 'Ausgetragen',
};

export function policyRegistrationLabel(status: PolicyRegistrationStatus): string {
  return POLICY_REGISTRATION_LABEL[status] ?? status;
}

function assertNever(x: never): never {
  throw new Error(`unhandled registration status ${String(x)}`);
}

/**
 * The registration status of one policy, derived (never stored): an exception wins, a policy without a
 * registration is not registered, a cancelled or expired policy is history, else the registration row's
 * status in Stefan's words. A rejected registration keeps its policies linked, so they read «nicht
 * courtageberechtigt». Pure and exhaustive.
 */
export function derivePolicyRegistrationStatus(
  policy: {
    status: PolicyStatus;
    registration_exception_reason: string | null;
    registration_inherited_from_policy_id: string | null;
    registration_id: string | null;
  },
  registration: { status: MandateInsurerNotificationStatus } | null,
): PolicyRegistrationStatus {
  if (policy.registration_exception_reason !== null) return 'ausgenommen';
  if (policy.registration_id === null || registration === null) return 'nicht_registriert';
  if (policy.status === 'cancelled' || policy.status === 'expired') return 'historisch';
  const s = registration.status;
  switch (s) {
    case 'sending':
    case 'zurueckgestellt':
    case 'zur_freigabe':
    case 'benachrichtigt':
      return 'gemeldet';
    case 'erinnert':
      return 'erinnert';
    case 'klaerung':
      return 'klaerung';
    case 'rueckfrage':
      return 'rueckfrage';
    case 'bestaetigt':
      return policy.registration_inherited_from_policy_id !== null ? 'eingetragen_vererbt' : 'eingetragen';
    case 'abgelehnt':
      return 'nicht_courtageberechtigt';
    case 'manuell_geschlossen':
      return 'geschlossen';
    case 'ausgetragen':
      return 'ausgetragen';
    case 'informiert':
      // an Auflösungsmeldung never links a policy; the source row carries ausgetragen
      return 'ausgetragen';
    default:
      return assertNever(s);
  }
}

// ── dates ───────────────────────────────────────────────────────────────────────────────────

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Days since the epoch of a `YYYY-MM-DD` (or an ISO timestamp's date part); null for another shape. */
function epochDay(value: string | null | undefined): number | null {
  const m = ISO_DATE.exec(value ?? '');
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(t);
  if (d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  return Math.round(t / 86_400_000);
}

/** An Austragungsdatum lies within this many days of the termination's Enddatum. */
export const DEREGISTRATION_DATE_WINDOW_DAYS = 366;

/**
 * Is an Austragungsdatum plausible: not before the mandate's signing day and within a year of the
 * termination's Enddatum (inclusive)? The registration window (signing date + 730 days) does not apply:
 * a long-standing mandate ends years after its signature. Pure.
 */
export function isPlausibleDeregistrationDate(signedAt: string | null, endDate: string | null, date: string): boolean {
  const d = epochDay(date);
  if (d === null) return false;
  const signed = epochDay(signedAt);
  if (signed !== null && d < signed) return false;
  const end = epochDay(endDate);
  if (end !== null && Math.abs(d - end) > DEREGISTRATION_DATE_WINDOW_DAYS) return false;
  return true;
}

/** A registration's Wirkungsdatum is plausible from the signing date minus this many days ... */
export const EFFECTIVE_DATE_WINDOW_BEFORE_DAYS = 365;
/** ... up to the signing date plus this many days. */
export const EFFECTIVE_DATE_WINDOW_AFTER_DAYS = 730;

/**
 * Is a parsed Wirkungsdatum plausible: from the mandate's signing date minus 365 days to plus 730 days
 * (inclusive)? A date that is not a valid `YYYY-MM-DD` is not. Without a signing date the window is
 * taken around today. Pure.
 */
export function isPlausibleEffectiveDate(signedAt: string | null | undefined, effectiveDate: string | null | undefined): boolean {
  const date = epochDay(effectiveDate);
  if (date === null) return false;
  const anchor = epochDay(signedAt) ?? epochDay(new Date().toISOString());
  if (anchor === null) return false;
  return date >= anchor - EFFECTIVE_DATE_WINDOW_BEFORE_DAYS && date <= anchor + EFFECTIVE_DATE_WINDOW_AFTER_DAYS;
}

/** The courtage eligibility of a policy that follows a registration: the later of its inception and the Wirkungsdatum. Pure. */
export function computeInheritedEligibilityFrom(inceptionDate: string | null | undefined, effectiveDate: string): string {
  const inception = (inceptionDate ?? '').slice(0, 10);
  if (epochDay(inception) === null) return effectiveDate;
  return inception > effectiveDate ? inception : effectiveDate;
}

// ── wire DTOs ───────────────────────────────────────────────────────────────────────────────

/** POST /mandates/:id/registrations/:regId/bestaetigen */
export interface BestaetigenRegistrationBody {
  effectiveDate?: string;
  brokerVuCode?: string;
  excludedPolicyIds?: string[];
  documentId?: string;
  communicationId?: string;
  notiz?: string;
}

/**
 * POST /mandates/:id/registrations/:regId/ablehnen. A registration (P5) needs the reason; «Austragung
 * verweigert» on an Auflösungsmeldung (P6) has none: its Bemerkung (notiz) and Beleg say what happened.
 */
export interface AblehnenRegistrationBody {
  reasonCode?: RegistrationRejectionReason;
  documentId?: string;
  communicationId?: string;
  notiz?: string;
}

/** POST /mandates/:id/registrations/:regId/rueckfrage */
export interface RueckfrageRegistrationBody {
  notiz: string;
}

/** POST /mandates/:id/registrations/:regId/rueckfrage-beantwortet */
export interface RueckfrageBeantwortetRegistrationBody {
  communicationId?: string;
}

/** POST /mandates/:id/registrations/:regId/korrektur */
export interface KorrekturRegistrationBody {
  reasonCode: RegistrationKorrekturReason;
  notiz?: string;
}

/** POST /policies/:policyId/registration-ausnahme */
export interface PolicyRegistrationAusnahmeBody {
  reason?: BrokerPolicyExceptionReason;
  lift?: boolean;
  /** Required when lifting (with mandate.eingangskontrolle on top of policy.write). */
  liftReasonCode?: RegistrationExceptionLiftReason;
  /** The Beleg of a lift (one of the two required), checked like a decision's. */
  documentId?: string;
  communicationId?: string;
}

/** GET /tasks/:taskId/registration-zuordnen */
export interface RegistrationZuordnenDto {
  communication_id: string;
  candidates: Array<{
    registration_id: string;
    mandate_id: string;
    carrier_name: string | null;
    vorgangs_ref: string;
    status: MandateInsurerNotificationStatus;
    status_label: string;
  }>;
}

/** GET /registrations/:regId/beleg-options: the mails a broker may pick as Beleg, paged. */
export interface RegistrationBelegOptionsDto {
  items: Array<{
    communication_id: string;
    subject: string | null;
    from: string | null;
    received_at: string | null;
    direction: string;
    attachments: Array<{ document_id: string; filename: string }>;
  }>;
  next_cursor: string | null;
}

/** POST /tasks/:taskId/registration-zuordnen */
export interface ZuordnenRegistrationBody {
  registrationId: string;
}

/** The answer of POST /policies/:policyId/registration-ausnahme. */
export interface PolicyRegistrationDto {
  policy_id: string;
  registration_id: string | null;
  registration_exception_reason: string | null;
  registration_excluded_at: string | null;
  courtage_eligible_from: string | null;
  registration_status: PolicyRegistrationStatus;
  registration_status_label: string;
}

/** What the parser proposed for a reply, as the review task shows it. */
export interface RegistrationProposal {
  outcome: RegistrationReplyOutcome;
  effectiveDate: string | null;
  brokerVuCode: string | null;
  excludedPolicyIds: string[];
  matchedPolicyNumbers: string[];
  rejectionReasonCode: string | null;
}

/** GET /tasks/:taskId/registration-proposal */
export interface RegistrationProposalDto {
  reply: {
    id: string;
    communication_id: string;
    match_method: RegistrationReplyMatch;
    match_method_label: string;
    match_score: number;
    outcome: RegistrationReplyOutcome;
    confidence: number;
    routing: string;
    parser: string;
    reason_text: string | null;
    decision: RegistrationReplyDecision | null;
  };
  registration: {
    id: string;
    mandate_id: string;
    carrier_uid: string;
    carrier_name: string | null;
    status: MandateInsurerNotificationStatus;
    status_label: string;
    requested_effective_date: string | null;
    broker_vu_code: string | null;
    policies: Array<{ policy_id: string; policy_number: string | null }>;
  };
  /** The mail the answer came in (the task carries it too). */
  mail: { communication_id: string; subject: string | null; from: string | null; received_at: string | null };
  /** Null when the parser had nothing usable (unklar, a failed parse): the broker decides from the mail. */
  proposal: RegistrationProposal | null;
  /** The codes that sent the reply to a human (effective_date_implausible, ...). */
  review_reasons: string[];
}

/** POST /tasks/:taskId/registration-proposal/apply */
export interface ApplyRegistrationProposalBody {
  accept: boolean;
  overrides?: {
    outcome?: 'eingetragen' | 'abgelehnt';
    effectiveDate?: string;
    brokerVuCode?: string;
    excludedPolicyIds?: string[];
    reasonCode?: RegistrationRejectionReason;
  };
}

/** The review task of a reply the parser could not settle (process mandate_registration). */
export const REGISTRATION_REVIEW_TASK_TITLE = 'Eintragungsbestätigung prüfen';

/** The activity events of the confirmation (Kap. 12; activity rows with meta.event). */
export const MANDATE_REGISTRATION_REPLY_EVENTS = {
  CONFIRMED: 'mandate_registration.confirmed',
  REJECTED: 'mandate_registration.rejected',
  RUECKFRAGE: 'mandate_registration.rueckfrage',
  KORREKTUR: 'mandate_registration.korrektur',
  POLICY_PROPAGATED: 'policy.registration_propagated',
} as const;
